import * as React from 'react';

import { Input, List, ScrollArea, Text } from '@/components/ui';

// Search — flat typeahead composition (Input + filtered List + optional
// summary footer). Composes ui/ primitives into a single search-to-pick
// interaction surface. Lives in `business/` because it composes ui/
// primitives into a higher-level interaction; the primitives themselves
// stay vocabulary-free.
//
// Search renders in document flow — no Popover, no portal, no floating
// chrome. The caller decides where to put it: inline on a page, inside
// a Popover anchored to a trigger button, inside a modal/dialog, etc.
//
// Earlier versions of Search baked in their own Popover so the results
// floated beneath the Input. That worked for standalone use but bit
// hard when the caller wanted Search inside their OWN Popover (e.g.
// the Plot's comparison search, anchored to a magnifying-glass icon):
// two nested Popovers each ran their own ResizeObserver chain and the
// browser logged "ResizeObserver loop completed with undelivered
// notifications" runtime errors on every interaction. Moving the
// popover decision to the caller keeps the API honest: Search is the
// pattern, the floating layer is a positioning choice.
//
// Caller owns the query state and the items list. Search owns nothing
// stateful — the results panel just appears when the trimmed query
// meets `minQueryLength` AND there is content to show (results or a
// summary).
//
// Items follow the List item shape — `{ value, label, description,
// leadingIcon, trailingIcon }`. Icons are resolved by the caller (e.g.
// `WiseMetering.icons.getIconFromKind(slug)` for indicator pickers),
// keeping Search itself free of domain mappings.
//
// All items are rendered into the List unchanged; `maxResults` only
// controls the visible viewport height so that exactly `maxResults`
// rows fit before scrolling. The viewport height is computed from the
// heights of the first `maxResults` items — rows with `description`
// are taller than rows without.

// Row heights match List's row chrome (py-2.5 padding + content from
// Typography). Numbers derived from the primitives, not eyeballed:
//   • base row       = 20px padding + 20px (control leading-5)            = 40px
//   • + description  = 20px padding + 20px (control) + 2px (gap-0.5)
//                       + 20px (body-sm leading-5)                         = 62px
const ROW_HEIGHT_PX = 40;
const ROW_HEIGHT_WITH_DESCRIPTION_PX = 62;

const Search = ({
    disabled = false,
    items = [],
    maxResults = 8,
    minQueryLength = 0,
    onSelect,
    onValueChange,
    placeholder,
    size = 'medium',
    summary = null,
    value
}) => {
    const query = (value ?? '').trim();
    const meetsThreshold = query.length >= minQueryLength;
    const hasResults = items.length > 0;
    const hasSummary = Boolean(summary);
    const shouldShowResults = meetsThreshold && (hasResults || hasSummary);

    // Sum the heights of the first `maxResults` items — that's how tall
    // the viewport needs to be so exactly that many rows fit before the
    // ScrollArea starts scrolling.
    const viewportHeightPx = items
        .slice(0, maxResults)
        .reduce((sum, item) => sum + (item.description ? ROW_HEIGHT_WITH_DESCRIPTION_PX : ROW_HEIGHT_PX), 0);

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <Input
                disabled={disabled}
                onChange={event => onValueChange?.(event.target.value)}
                placeholder={placeholder}
                size={size}
                type="search"
                value={value ?? ''}
            />
            {shouldShowResults ? (
                // The results panel sits in document flow with no chrome of its
                // own — no border, no rounded, no shadow, no background. Earlier
                // it painted floating-surface chrome, but that doubled the
                // border / shadow that the caller's container (Popover, Dialog,
                // or Surface in catalog demos) already provides. The caller
                // owns the floating chrome; Search owns the typeahead behavior.
                <div className="tw-flex tw-flex-col">
                    {hasResults ? (
                        <div style={{ height: `${viewportHeightPx}px` }}>
                            <ScrollArea>
                                <List
                                    items={items}
                                    onSelect={pickedValue => onSelect?.(pickedValue)}
                                />
                            </ScrollArea>
                        </div>
                    ) : null}
                    {hasSummary ? (
                        <div className="tw-px-3 tw-py-2">
                            <Text variant="caption" tone="muted">{summary}</Text>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

Search.displayName = 'Search';

export { Search };
